import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import ImageShimmer from './ImageShimmer';
import InitialsAvatar from './InitialsAvatar';
import { font } from '../../utils/fontHelper';
import type { Story } from '../../services/storiesService';

const TMDB_POSTER_BASE = 'https://image.tmdb.org/t/p/w185';

interface StoryRailItemProps {
  authorUid: string;
  authorName: string;
  avatarUrl?: string;
  stories: Story[];
  hasUnseen: boolean;
  onClick: () => void;
}

const StoryRailItem: React.FC<StoryRailItemProps> = ({
  authorName,
  avatarUrl,
  stories,
  hasUnseen,
  onClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const visiblePosters = stories.slice(0, 4);
  const extraCount = stories.length - 4;
  const hasMore = extraCount > 0;
  const POSTER_SIZE = 80;
  const OFFSET = 14;
  const RING_WIDTH = 2.5;
  const GAP = 5;
  const AVATAR_OVERLAY_SIZE = 24;
  const stackWidth = POSTER_SIZE + (visiblePosters.length - 1) * OFFSET;

  const bgColor = isDark ? '#111' : '#fff';
  const unseenGradient = `linear-gradient(135deg, ${theme.palette.primary.main}, #ff6482)`;
  const seenColor = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)';

  const firstName = authorName ? authorName.split(' ')[0] : '';

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        p: 0.5,
        borderRadius: 2,
        width: stackWidth,
        minWidth: 72,
        flexShrink: 0,
        '&:active': { transform: 'scale(0.95)' },
        transition: 'transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Poster stack */}
      <Box sx={{ position: 'relative', width: stackWidth, height: POSTER_SIZE }}>
        {visiblePosters.map((story, i) => {
          const isNewest = i === 0;
          const zIndex = visiblePosters.length - i;
          const ringBg = isNewest && hasUnseen ? unseenGradient : seenColor;
          const rawPoster = story.kind === 'watchlist' ? story.coverPath : story.posterPath;
          const posterUrl = rawPoster ? `${TMDB_POSTER_BASE}${rawPoster}` : null;
          const altText = story.kind === 'watchlist' ? story.watchlistName : story.movieTitle;

          return (
            <Box
              key={story.id}
              sx={{
                position: 'absolute',
                left: i * OFFSET,
                top: 0,
                width: POSTER_SIZE,
                height: POSTER_SIZE,
                borderRadius: '50%',
                zIndex,
                background: ringBg,
                padding: `${RING_WIDTH}px`,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: bgColor,
                  padding: `${GAP}px`,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  }}
                >
                  {posterUrl ? (
                    <ImageShimmer
                      src={posterUrl}
                      alt={altText}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: 18 }}>🎬</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Author avatar overlay — horizontally centred above the name */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: -2,
            width: AVATAR_OVERLAY_SIZE,
            height: AVATAR_OVERLAY_SIZE,
            borderRadius: '50%',
            border: `2px solid ${bgColor}`,
            overflow: 'hidden',
            zIndex: visiblePosters.length + 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          }}
        >
          <InitialsAvatar
            src={avatarUrl}
            name={authorName}
            size={AVATAR_OVERLAY_SIZE - 4}
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>

        {/* +N badge — bottom-right of stack */}
        {hasMore && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              bgcolor: isDark ? 'rgba(40,32,60,0.92)' : 'rgba(240,236,255,0.92)',
              border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '10px',
              px: '5px',
              py: '1px',
              zIndex: visiblePosters.length + 1,
              lineHeight: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: font(),
                fontSize: 10,
                fontWeight: 700,
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)',
              }}
            >
              +{extraCount}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Author name — plain text below the stack */}
      {firstName ? (
        <Typography
          sx={{
            fontFamily: font(),
            fontSize: 12,
            color: hasUnseen ? theme.palette.text.primary : theme.palette.text.secondary,
            fontWeight: hasUnseen ? 600 : 400,
            maxWidth: stackWidth + 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {firstName}
        </Typography>
      ) : (
        <Skeleton variant="text" width={48} height={14} />
      )}
    </Box>
  );
};

export default StoryRailItem;
